/**
 * Voice WebSocket frame codec - mirrors bridge/src/voice/voiceWsFrames.ts.
 * Keep them in lockstep when the format evolves.
 */

export const FrameType = {
  AUDIO_UP: 1,
  AUDIO_FWD: 2,
  HEARTBEAT: 3,
} as const

export interface AudioFwdFrame {
  readonly type: 'audioFwd'
  readonly senderSessionId: number
  readonly agid: string
  readonly opus: Uint8Array
}

export interface HeartbeatFrame {
  readonly type: 'heartbeat'
}

export type DownlinkFrame = AudioFwdFrame | HeartbeatFrame

/**
 * Encode an uplink (mic → bridge) frame. Layout:
 *   [type:u8=AUDIO_UP][seq:u32 LE][opus...]
 */
export function encodeAudioUp(seq: number, opus: Uint8Array): ArrayBuffer {
  const out = new Uint8Array(1 + 4 + opus.length)
  out[0] = FrameType.AUDIO_UP
  const view = new DataView(out.buffer)
  view.setUint32(1, seq >>> 0, true)
  out.set(opus, 5)
  return out.buffer
}

export function encodeHeartbeat(): ArrayBuffer {
  return new Uint8Array([FrameType.HEARTBEAT]).buffer
}

/**
 * Parse an inbound frame. Returns null on malformed input - caller drops.
 */
export function decodeDownlink(buf: ArrayBuffer): DownlinkFrame | null {
  if (buf.byteLength < 1) return null
  const bytes = new Uint8Array(buf)
  const type = bytes[0]
  if (type === FrameType.HEARTBEAT) return { type: 'heartbeat' }
  if (type !== FrameType.AUDIO_FWD) return null
  if (bytes.length < 1 + 4 + 1) return null
  const view = new DataView(buf)
  const senderSessionId = view.getUint32(1, true)
  const agidLen = bytes[5]!
  if (bytes.length < 6 + agidLen) return null
  const agidBytes = bytes.subarray(6, 6 + agidLen)
  const agid = new TextDecoder('utf-8', { fatal: false }).decode(agidBytes)
  const opus = bytes.subarray(6 + agidLen)
  if (opus.length === 0) return null
  return { type: 'audioFwd', senderSessionId, agid, opus }
}
